import { useState, useCallback, useMemo, useEffect } from 'react';
import AuthContext from './AuthContext';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    fetchCurrentUser,
    performLogout,
    logout,
    restoreSession,
} from '@/features/auth/store/authSlice';
import { getAccessToken, getRefreshToken } from '@/lib/authSession';
import { store } from '@/store';
import {
    selectIsAuthenticated,
    selectContextUser,
} from '@/features/auth/store/authSelectors';

/**
 * Auth UI context derived from Redux. Session restore runs once on mount.
 */
export function AuthProvider({ children }) {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectContextUser);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const auth = store.getState().auth;
                const alreadySignedIn =
                    auth.isAuthenticated &&
                    auth.user?.user?.id &&
                    getAccessToken();
                // Avoid a redundant GET /auth/me right after login (same tab) — a failure here cleared the session.
                if (alreadySignedIn) {
                    if (!cancelled) setHydrated(true);
                    return;
                }
                if (getAccessToken()) {
                    await dispatch(fetchCurrentUser()).unwrap();
                } else if (getRefreshToken()) {
                    await dispatch(restoreSession()).unwrap();
                }
            } catch {
                const hadUser = store.getState().auth.user?.user?.id;
                if (!hadUser) {
                    dispatch(logout());
                }
            }
            if (!cancelled) setHydrated(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    const logoutFn = useCallback(() => {
        dispatch(performLogout());
    }, [dispatch]);

    const updateUser = useCallback(() => {
        dispatch(fetchCurrentUser());
    }, [dispatch]);

    const isLoading = !hydrated;

    const value = useMemo(
        () => ({
            user,
            isAuthenticated,
            isLoading,
            logout: logoutFn,
            /** Refetch account from GET /accounts/:id (e.g. after profile change) */
            updateUser,
        }),
        [user, isAuthenticated, isLoading, logoutFn, updateUser]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
